import React, { useContext, useEffect, useState, useMemo } from "react";
import { 
  Box, 
  Typography, 
  Paper, 
  IconButton, 
  Stack,
  alpha
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CreateSuperAdminDialog from "../CreateSuperAdminDialog";
import EditAdminDialog from "../EditAdminDialog";
import AdminDetailsDialog from "../AdminDetailsDialog";
import { SnackbarContext } from "components/snackBar/SnackbarContext";
import CustomPagination from "views/client/pagination";
import { 
  ViewModuleOutlined, 
  ViewListOutlined
} from "@mui/icons-material";
import NewSearchBar from "components/searchBars/NewSearchBar";
import { useTranslation } from "react-i18next";
import { PostSearchBar } from "components/searchBars/SearchBars";
import ModernAdminCard from "../ModernAdminCard"; // Import the new card component
import searchIcon from "../../../assets/img/search_icon.svg";
import addIcon from "../../../assets/img/add_icon.svg";
import filterIcon from "../../../assets/img/filter_icon.svg";

// Midnight blue theme colors
const colors = {
  primary: '#1e293b',
  secondary: '#334155',
  accent: '#0ea5e9',
  text: '#64748b',
  lightText: '#94a3b8',
  background: '#f8fafc',
};

const MainContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: "transparent",
  minHeight: "100vh",
}));

const HeaderSection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  background: "linear-gradient(135deg, #ffffff 0%, #fafbff 100%)",
  border: `1px solid ${alpha(colors.primary, 0.08)}`,
  boxShadow: `0 8px 32px ${alpha(colors.primary, 0.08)}`,
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  padding: theme.spacing(1),
  borderRadius: 8,
  backgroundColor: active ? alpha(colors.primary, 0.1) : "transparent",
  color: active ? colors.primary : alpha(colors.primary, 0.5),
  border: `1px solid ${active ? colors.primary : alpha(colors.primary, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(colors.primary, 0.05),
    color: colors.primary,
  },
}));

const GridContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(380px, 1fr))",
  gap: theme.spacing(3),
  padding: theme.spacing(1),
  backgroundColor: "transparent",
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
  },
}));

const ListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(1),
}));

const SuperAdminCreateSuperAdmin = () => {
  const { showSnackbar } = useContext(SnackbarContext);
  
  
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    company: '',
    address: '',
    city: '',
    country: '',
    postal_code: '',
    image: '',
  });
  
  const [superAdmins, setSuperAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [viewMode, setViewMode] = useState('grid'); // Changed default to grid
  const [filter, setFilter] = useState("");
  const [openCreate, setOpenCreate] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { t } = useTranslation();

  const formCompletionPercentage = useMemo(() => {
    const requiredFields = ['username', 'email', 'password', 'first_name', 'last_name'];
    const filledRequired = requiredFields.filter(field => form[field].trim() !== '').length;
    const optionalFields = ['company', 'address', 'city', 'country', 'postal_code', 'image'];
    const filledOptional = optionalFields.filter(field => form[field].trim() !== '').length;

    const requiredPercentage = (filledRequired / requiredFields.length) * 70;
    const optionalPercentage = (filledOptional / optionalFields.length) * 30;

    return Math.round(requiredPercentage + optionalPercentage);
  }, [form]);

  const normalizeUser = (u) => ({
    ...u,
    username: u?.username ?? u?.user_name ?? u?.user?.username ?? '',
    postal_code: u?.postal_code ?? u?.postalCode ?? u?.zip_code ?? u?.zip ?? u?.address?.postal_code ?? '',
    // Normalize creator information to a common key used by the card
    created_by:
      u?.created_by?.username ??
      u?.created_by ??
      u?.createdBy ??
      u?.created_user ??
      u?.created?.username ??
      u?.creator?.username ??
      u?.created_by_name ??
      ''
  });

  const fetchSuperAdmins = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}authentication/superadmin/list/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const list = Array.isArray(data?.results || data) ? (data.results || data) : [];
      const normalized = list.map(normalizeUser);
      setSuperAdmins(normalized);
      setPage(0);
    } catch (e) {
      console.error(e);
      setSuperAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL;
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${apiUrl}authentication/superadmin/create-superadmin/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error('Failed to create super admin');
      showSnackbar && showSnackbar('SuperAdmin created successfully', { severity: 'success' });
      setForm({ username: '', email: '', password: '', first_name: '', last_name: '', company: '', address: '', city: '', country: '', postal_code: '', image: '' });
      setOpenCreate(false);
      fetchSuperAdmins();
    } catch (e) {
      console.error(e);
      showSnackbar && showSnackbar(e.message || 'Error creating super admin', { severity: 'error' });
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleViewSuperAdmin = (superAdmin) => {
    setSelectedItem(superAdmin);
    setDetailsOpen(true);
  };

  const handleEditSuperAdmin = (superAdmin) => {
    setSelectedItem(superAdmin);
    setEditOpen(true);
  };

  const filteredSuperAdmins = superAdmins.filter(sa => 
    `${sa.first_name || ''} ${sa.last_name || ''} ${sa.email || ''}`.toLowerCase().includes(filter.toLowerCase())
  );

  const paginatedSuperAdmins = filteredSuperAdmins.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    fetchSuperAdmins();
  }, []);

  return (
    <MainContainer>
      
      
      <Box p={3} sx={{ pt: 0 }}>
        <Box sx={{ mb: 2 }}>
          <NewSearchBar
            admin={true}
            filter={filter}
            setFilter={setFilter}
            setOpen={setOpenCreate}
            t={t}
            searchIcon={searchIcon}
            addIcon={addIcon}
            filterIcon={filterIcon}
            PostSearchBar={PostSearchBar}
            handleSearch={() => {}}
            addLabel={t('postPageMeta.addSuperAdminButton')}
            searchPlaceholder={t('postPageMeta.filterSuperAdmin')}
            searchLabel={t('postPage.search')}
          />
        </Box>

        <CreateSuperAdminDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          form={form}
          setForm={setForm}
          onSubmit={handleSubmit}
          submitLoading={submitLoading}
          completion={formCompletionPercentage}
        />

        <AdminDetailsDialog
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          item={selectedItem}
          userType="superadmin"
        />

        <EditAdminDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          item={selectedItem}
          userType="superadmin"
          onUpdated={() => { setEditOpen(false); fetchSuperAdmins(); }}
        />

        <Box mt={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
            <Typography sx={{ fontFamily: 'Poppins', fontWeight: 600, fontSize: 18, color: colors.primary }}>
              {t('superAdmin.screens.createSuperAdmin.existingSuperAdmins')} ({filteredSuperAdmins.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ViewToggleButton active={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
                <ViewModuleOutlined fontSize="small" />
              </ViewToggleButton>
              <ViewToggleButton active={viewMode === 'list'} onClick={() => setViewMode('list')}>
                <ViewListOutlined fontSize="small" />
              </ViewToggleButton>
            </Box>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ color: colors.text, fontFamily: 'Poppins' }}>{t('superAdmin.screens.createSuperAdmin.loading')}</Typography>
            </Box>
          ) : filteredSuperAdmins.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <Typography sx={{ color: colors.text, fontFamily: 'Poppins' }}>{t('superAdmin.screens.createSuperAdmin.noSuperAdminsFound')}</Typography>
            </Box>
          ) : (
            <>
              {viewMode === 'grid' ? (
                <GridContainer>
                  {paginatedSuperAdmins.map((superAdmin) => (
                    <ModernAdminCard
                      key={superAdmin.id}
                      item={superAdmin}
                      onView={handleViewSuperAdmin}
                      onEdit={handleEditSuperAdmin}
                      userType="superadmin"
                      showCreatedBy={true}
                    />
                  ))}
                </GridContainer>
              ) : (
                <ListContainer>
                  {paginatedSuperAdmins.map((superAdmin) => (
                    <ModernAdminCard
                      key={superAdmin.id}
                      item={superAdmin}
                      onView={handleViewSuperAdmin}
                      onEdit={handleEditSuperAdmin}
                      userType="superadmin"
                      showCreatedBy={true}
                    />
                  ))}
                </ListContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                <CustomPagination
                  page={page}
                  handleChangePage={(e, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                  count={filteredSuperAdmins.length}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    </MainContainer>
  );
};

export default SuperAdminCreateSuperAdmin;